import { useCallback, useRef, useState } from 'react';

interface UseConfirmableActionOptions<T> {
  onApply: (payload: T) => void;
}

interface UseConfirmableActionResult<T> {
  isOpen: boolean;
  request: (payload: T) => void;
  confirm: () => void;
  cancel: () => void;
}

export function useConfirmableAction<T>({ onApply }: UseConfirmableActionOptions<T>): UseConfirmableActionResult<T> {
  // Ref (not state) so a repeated confirm before re-render can't apply twice; boxed so void payloads count as pending
  const pendingRef = useRef<{ payload: T } | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const request = useCallback((payload: T) => {
    pendingRef.current = { payload };
    setIsOpen(true);
  }, []);

  const confirm = useCallback(() => {
    const pending = pendingRef.current;
    if (!pending) {
      return;
    }
    pendingRef.current = null;
    setIsOpen(false);
    onApply(pending.payload);
  }, [onApply]);

  const cancel = useCallback(() => {
    pendingRef.current = null;
    setIsOpen(false);
  }, []);

  return {
    isOpen,
    request,
    confirm,
    cancel
  };
}
